import type {
  EngagementIndex,
  QuestionChartType,
  QuestionDataNature,
  QuestionDemographicDimension,
} from '@brushfire/types'

/**
 * BFP-485 — shared helpers for the Individual Question Drill-Down Dashboard.
 * Single source of truth for chart-type selection, age bucketing, engagement
 * index, and deterministic text analytics (word cloud / theme frequency).
 */

/** Canonical age bands used for the `By Age` demographic breakdown. */
export const AGE_BUCKETS = [
  '18-24',
  '25-34',
  '35-44',
  '45-54',
  '55-64',
  '65_plus',
] as const

export type AgeBucket = (typeof AGE_BUCKETS)[number]

/** Maps an exact age to a canonical band. Returns null for non-finite / under-18. */
export function ageToBucket(age: number | null | undefined): AgeBucket | null {
  if (age == null || !Number.isFinite(age) || age < 18) return null
  if (age <= 24) return '18-24'
  if (age <= 34) return '25-34'
  if (age <= 44) return '35-44'
  if (age <= 54) return '45-54'
  if (age <= 64) return '55-64'
  return '65_plus'
}

/** Computes age in whole years from a date of birth (ISO string or Date). */
export function ageFromDateOfBirth(
  dateOfBirth: string | Date | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!dateOfBirth) return null
  const dob = dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth)
  if (Number.isNaN(dob.getTime())) return null
  let age = now.getFullYear() - dob.getFullYear()
  const monthDelta = now.getMonth() - dob.getMonth()
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < dob.getDate())) {
    age -= 1
  }
  if (age < 0 || age > 120) return null
  return age
}

/** Quantitative vs qualitative classification (BR-QD-09/10). */
const QUALITATIVE_TYPES = new Set(['short_text', 'long_text', 'video'])

export function questionDataNature(type: string): QuestionDataNature {
  return QUALITATIVE_TYPES.has(type.trim().toLowerCase()) ? 'qualitative' : 'quantitative'
}

const QUESTION_CHART_TYPES: Record<
  string,
  { base: QuestionChartType; filtered: QuestionChartType }
> = {
  multiple_choice: { base: 'donut_pie', filtered: 'grouped_horizontal_bar' },
  single_choice: { base: 'donut_pie', filtered: 'grouped_column' },
  dropdown: { base: 'donut_pie', filtered: 'grouped_horizontal_bar' },
  checkbox: { base: 'horizontal_bar', filtered: 'grouped_column' },
  yes_no: { base: 'donut_pie', filtered: 'grouped_column' },
  picture_choice: { base: 'donut_pie', filtered: 'grouped_column' },
  rating: { base: 'vertical_column', filtered: 'stacked_horizontal_bar' },
  likert: { base: 'stacked_horizontal_bar', filtered: 'stacked_horizontal_bar' },
  ranking: { base: 'stacked_horizontal_bar', filtered: 'stacked_horizontal_bar' },
  slider: { base: 'histogram', filtered: 'multi_line' },
  date: { base: 'line_area', filtered: 'multi_line' },
  short_text: { base: 'word_cloud', filtered: 'theme_frequency_bar' },
  long_text: { base: 'word_cloud', filtered: 'theme_frequency_bar' },
  video: { base: 'word_cloud', filtered: 'theme_frequency_bar' },
}

const DEFAULT_CHART_TYPES = {
  base: 'horizontal_bar' as QuestionChartType,
  filtered: 'grouped_column' as QuestionChartType,
}

/**
 * Resolves the auto-selected chart type for a question (BR-QD-08/09/10/11).
 * `hasFilter` selects the grouped / multi-series variant used when a
 * demographic filter is active.
 */
export function resolveQuestionChartType(type: string, hasFilter: boolean): QuestionChartType {
  const mapping = QUESTION_CHART_TYPES[type.trim().toLowerCase()] ?? DEFAULT_CHART_TYPES
  return hasFilter ? mapping.filtered : mapping.base
}

/** Default top-N for ranked high-cardinality demographic composition bars (BFP-536). */
export const DEMOGRAPHIC_RANKED_TOP_N = 10

const DEMOGRAPHIC_CHART_TYPES: Record<QuestionDemographicDimension, QuestionChartType> = {
  age: 'histogram',
  gender: 'donut_pie',
  region: 'horizontal_bar',
  city: 'horizontal_bar',
  education: 'horizontal_bar',
  deviceType: 'donut_pie',
  employment: 'horizontal_bar',
  householdSize: 'vertical_column',
  numberOfChildren: 'vertical_column',
  industry: 'horizontal_bar',
  maritalStatus: 'horizontal_bar',
  housingStatus: 'donut_pie',
}

const RANKED_DEMOGRAPHIC_DIMENSIONS = new Set<QuestionDemographicDimension>([
  'region',
  'city',
  'industry',
])

/**
 * BFP-536 — chart type for All-tab demographic composition cards (keyed by
 * demographic dimension data shape, not question type).
 */
export function resolveDemographicChartType(
  dimension: QuestionDemographicDimension,
): QuestionChartType {
  return DEMOGRAPHIC_CHART_TYPES[dimension]
}

/** True when the dimension uses ranked horizontal bar with top-N + expand (BFP-536). */
export function isRankedDemographicChart(dimension: QuestionDemographicDimension): boolean {
  return RANKED_DEMOGRAPHIC_DIMENSIONS.has(dimension)
}

/** Demographic cardinality / shape tier (BFP-543 reference table). */
export type DemographicCategoryTier = 'low' | 'mid' | 'high' | 'ordered'

const LOW_CARDINALITY_DIMENSIONS = new Set<QuestionDemographicDimension>([
  'gender',
  'deviceType',
  'housingStatus',
])

const MID_CARDINALITY_DIMENSIONS = new Set<QuestionDemographicDimension>([
  'education',
  'employment',
  'maritalStatus',
])

const ORDERED_NUMERIC_DIMENSIONS = new Set<QuestionDemographicDimension>([
  'age',
  'householdSize',
  'numberOfChildren',
])

/** Maps a demographic dimension to its BFP-543 category tier. */
export function resolveDemographicCategoryTier(
  dimension: QuestionDemographicDimension,
): DemographicCategoryTier {
  if (LOW_CARDINALITY_DIMENSIONS.has(dimension)) return 'low'
  if (MID_CARDINALITY_DIMENSIONS.has(dimension)) return 'mid'
  if (ORDERED_NUMERIC_DIMENSIONS.has(dimension)) return 'ordered'
  return 'high'
}

type CrossTabQuestionFamily =
  | 'discrete_choice'
  | 'rating_likert'
  | 'slider'
  | 'ranking'
  | 'date'
  | 'text'

const DISCRETE_CHOICE_TYPES = new Set([
  'multiple_choice',
  'single_choice',
  'yes_no',
  'dropdown',
  'checkbox',
  'picture_choice',
])

function resolveCrossTabQuestionFamily(type: string): CrossTabQuestionFamily {
  const normalized = type.trim().toLowerCase()
  if (DISCRETE_CHOICE_TYPES.has(normalized)) return 'discrete_choice'
  if (normalized === 'rating' || normalized === 'likert') return 'rating_likert'
  if (normalized === 'slider') return 'slider'
  if (normalized === 'ranking') return 'ranking'
  if (normalized === 'date') return 'date'
  if (normalized === 'short_text' || normalized === 'long_text' || normalized === 'video') {
    return 'text'
  }
  return 'discrete_choice'
}

/**
 * BFP-543 — chart type for demographic filter tabs (question answers × segment).
 * Donut/pie is never returned here; use `resolveDemographicChartType` for
 * All-tab composition-only cards.
 */
const CROSS_TAB_CHART_MATRIX: Record<
  CrossTabQuestionFamily,
  Record<DemographicCategoryTier, QuestionChartType>
> = {
  discrete_choice: {
    low: 'grouped_column',
    mid: 'grouped_column',
    high: 'grouped_horizontal_bar',
    ordered: 'grouped_column',
  },
  rating_likert: {
    low: 'stacked_horizontal_bar',
    mid: 'stacked_horizontal_bar',
    high: 'stacked_horizontal_bar',
    ordered: 'stacked_horizontal_bar',
  },
  slider: {
    low: 'vertical_column',
    mid: 'vertical_column',
    high: 'vertical_column',
    ordered: 'multi_line',
  },
  ranking: {
    low: 'stacked_horizontal_bar',
    mid: 'stacked_horizontal_bar',
    high: 'stacked_horizontal_bar',
    ordered: 'stacked_horizontal_bar',
  },
  date: {
    low: 'multi_line',
    mid: 'multi_line',
    high: 'multi_line',
    ordered: 'multi_line',
  },
  text: {
    low: 'theme_frequency_bar',
    mid: 'theme_frequency_bar',
    high: 'theme_frequency_bar',
    ordered: 'theme_frequency_bar',
  },
}

export function resolveCrossTabChartType(
  questionType: string,
  dimension: QuestionDemographicDimension,
): QuestionChartType {
  const family = resolveCrossTabQuestionFamily(questionType)
  const tier = resolveDemographicCategoryTier(dimension)
  return CROSS_TAB_CHART_MATRIX[family][tier]
}

/** True when the cross-tab matrix applies top-N segment ranking (high-cardinality tier). */
export function isRankedCrossTabChart(
  _questionType: string,
  dimension: QuestionDemographicDimension,
): boolean {
  return resolveDemographicCategoryTier(dimension) === 'high'
}

export interface EngagementIndexInput {
  /** True when the instrument has no optional questions (BR-QD-04). */
  mandatoryOnly: boolean
  /** Respondents who skipped at least one optional question (TC-QD-08 / BR-QD-04). */
  respondentsSkippingOptional: number
}

/**
 * TC-QD-06/07/08, BR-QD-04 — engagement index.
 * High: mandatory-only instrument, or every respondent answered all optional questions.
 * Average: at least one respondent skipped an optional question.
 */
export function computeEngagementIndex(input: EngagementIndexInput): EngagementIndex {
  if (input.mandatoryOnly) return 'High'
  return input.respondentsSkippingOptional > 0 ? 'Average' : 'High'
}

/** Common English stop words excluded from the word cloud (TC-QD-24). */
export const WORD_CLOUD_STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'been', 'but', 'by', 'can', 'could',
  'did', 'do', 'does', 'for', 'from', 'had', 'has', 'have', 'he', 'her', 'his',
  'i', 'if', 'in', 'into', 'is', 'it', 'its', 'me', 'my', 'no', 'not', 'of',
  'on', 'or', 'our', 'she', 'so', 'than', 'that', 'the', 'their', 'them', 'then',
  'there', 'these', 'they', 'this', 'to', 'too', 'us', 'was', 'we', 'were',
  'what', 'when', 'which', 'who', 'will', 'with', 'would', 'you', 'your',
])

function stripNonWordChars(text: string): string {
  let out = ''
  for (const ch of text.toLowerCase()) {
    const isAlphaNum =
      (ch >= 'a' && ch <= 'z') || (ch >= '0' && ch <= '9') || ch === ' ' || ch === "'"
    out += isAlphaNum ? ch : ' '
  }
  return out
}

function trimEdgeApostrophes(word: string): string {
  let start = 0
  let end = word.length
  while (start < end && word[start] === "'") start += 1
  while (end > start && word[end - 1] === "'") end -= 1
  return word.slice(start, end)
}

function tokenize(text: string): string[] {
  return stripNonWordChars(text)
    .split(/\s+/)
    .map(trimEdgeApostrophes)
    .filter(Boolean)
}

/**
 * Builds a frequency-ranked word cloud term list from free-text responses,
 * excluding stop words and very short tokens (TC-QD-24).
 */
export function computeWordCloudTerms(
  texts: string[],
  limit = 50,
): Array<{ word: string; count: number }> {
  const counts = new Map<string, number>()
  for (const text of texts) {
    if (!text) continue
    for (const token of tokenize(text)) {
      if (token.length < 3) continue
      if (WORD_CLOUD_STOP_WORDS.has(token)) continue
      counts.set(token, (counts.get(token) ?? 0) + 1)
    }
  }
  return [...counts.entries()]
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count || a.word.localeCompare(b.word))
    .slice(0, limit)
}

/** Positive / negative sentiment lexicons for deterministic Long Text scoring. */
const POSITIVE_LEXICON = new Set([
  'good', 'great', 'love', 'loved', 'like', 'liked', 'excellent', 'amazing',
  'best', 'happy', 'satisfied', 'awesome', 'enjoy', 'enjoyed', 'fantastic',
  'wonderful', 'helpful', 'easy', 'positive', 'recommend', 'perfect', 'nice',
])
const NEGATIVE_LEXICON = new Set([
  'bad', 'hate', 'hated', 'dislike', 'disliked', 'terrible', 'awful', 'worst',
  'poor', 'unhappy', 'dissatisfied', 'difficult', 'hard', 'confusing', 'slow',
  'negative', 'expensive', 'broken', 'disappointing', 'disappointed', 'frustrating',
])

/** Classifies a single free-text response as positive / neutral / negative. */
export function classifyTextSentiment(text: string): 'positive' | 'neutral' | 'negative' {
  let score = 0
  for (const token of tokenize(text)) {
    if (POSITIVE_LEXICON.has(token)) score += 1
    else if (NEGATIVE_LEXICON.has(token)) score -= 1
  }
  if (score > 0) return 'positive'
  if (score < 0) return 'negative'
  return 'neutral'
}

/** Aggregates sentiment counts across a set of Long Text responses. */
export function computeSentimentDistribution(texts: string[]): {
  positive: number
  neutral: number
  negative: number
} {
  const out = { positive: 0, neutral: 0, negative: 0 }
  for (const text of texts) {
    if (!text) continue
    out[classifyTextSentiment(text)] += 1
  }
  return out
}
